import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChildren,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

export interface TabItem {
  id: string;
  label: string;
  icon?: string;
  hasUpdate?: boolean;
  /** Numeric badge on the tab. Hidden at 0 or when omitted. Takes precedence
   * over {@link hasUpdate} so a count is never also a blue dot. */
  badgeCount?: number;
}

/**
 * Tab IDs that are always pinned and cannot be unpinned by the user (when not overridden by mandatoryTabIds input).
 * These always appear first in the main row in canonical order.
 */
export const MANDATORY_TAB_IDS: readonly string[] = ['client-chat', 'actions', 'finances', 'accounts'];

const STORAGE_KEY_PREFIX = 'bn.tabbar.pinnedTabs';
const STORAGE_VERSION = 1;

/** localStorage key for pinned tabs; optional context isolates FA vs default tab sets. */
export function tabBarPinnedStorageKey(context?: string | null): string {
  const ctx = context?.trim();
  return ctx ? `${STORAGE_KEY_PREFIX}.${ctx}.v1` : `${STORAGE_KEY_PREFIX}.v1`;
}

interface PinnedTabsStorage {
  pinned: string[];
  version: number;
}

/** Load and validate pinned tab IDs from localStorage. Returns null on any failure. */
function loadPinnedFromStorage(storageKey: string): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !Array.isArray((parsed as PinnedTabsStorage).pinned) ||
      (parsed as PinnedTabsStorage).version !== STORAGE_VERSION
    ) {
      return null;
    }
    const pinned = (parsed as PinnedTabsStorage).pinned;
    if (!pinned.every((id) => typeof id === 'string')) return null;
    return pinned;
  } catch {
    return null;
  }
}

/**
 * Persist the current pinned set to localStorage.
 * Only stores user-chosen optional tab IDs — mandatory IDs are excluded
 * because they are always re-merged at init and don't need to be stored.
 *
 * @param mandatoryIds - tab IDs that are always pinned (not stored).
 */
function savePinnedToStorage(
  storageKey: string,
  pinned: string[],
  mandatoryIds: readonly string[]
): void {
  try {
    const optionalOnly = pinned.filter((id) => !mandatoryIds.includes(id));
    const data: PinnedTabsStorage = { pinned: optionalOnly, version: STORAGE_VERSION };
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch {
    // Storage quota or private-browsing restriction — silently ignore.
  }
}

@Component({
  selector: 'bn-tab-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tab-bar.component.html',
  styleUrl: './tab-bar.component.scss',
  host: {
    class: 'bn-tab-bar-host',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class TabBarComponent implements AfterViewInit, OnDestroy {
  tabs = input.required<TabItem[]>();
  activeTabId = input.required<string>();
  /** When set, overrides MANDATORY_TAB_IDS for this instance (e.g. FA layout with profile). */
  mandatoryTabIds = input<string[] | undefined>();
  /**
   * Optional storage namespace so multiple tab-bar instances (FA vs default) do not
   * share pinned preferences. Folded into {@link tabBarPinnedStorageKey}.
   */
  storageContext = input<string | undefined>();

  tabSelect = output<string>();

  /** Effective mandatory IDs: input value or default MANDATORY_TAB_IDS. */
  readonly effectiveMandatoryIds = computed(() => this.mandatoryTabIds() ?? [...MANDATORY_TAB_IDS]);

  private storageKey(): string {
    return tabBarPinnedStorageKey(this.storageContext());
  }

  visibleTabs = signal<TabItem[]>([]);
  overflowTabs = signal<TabItem[]>([]);
  showOverflow = signal(false);

  /**
   * The set of pinned tab IDs (mandatory + user-pinned optional tabs).
   * Always includes effectiveMandatoryIds regardless of storage state.
   * Initialized in an effect so mandatoryTabIds input is available.
   */
  readonly pinnedTabIds = signal<Set<string>>(new Set());

  private _pinnedInitDone = false;

  /**
   * Tabs in canonical order that are pinned (mandatory + user-pinned optional).
   * These are the only candidates for the visible main row.
   */
  readonly pinnedTabs = computed<TabItem[]>(() => {
    const all = this.tabs();
    const pinned = this.pinnedTabIds();
    return all.filter((t) => pinned.has(t.id));
  });

  /**
   * Tabs in canonical order that are NOT pinned.
   * These always live in the More menu regardless of available width.
   */
  readonly unpinnedTabs = computed<TabItem[]>(() => {
    const all = this.tabs();
    const pinned = this.pinnedTabIds();
    return all.filter((t) => !pinned.has(t.id));
  });

  /** True when the menu must remain available for optional pin management. */
  readonly hasManageableTabs = computed<boolean>(() => {
    const mandatory = new Set(this.effectiveMandatoryIds());
    return this.tabs().some((tab) => !mandatory.has(tab.id));
  });

  /**
   * All tabs in canonical order (pinned first, then unpinned).
   * Used by the More menu to list every tab.
   */
  readonly orderedTabs = computed<TabItem[]>(() => [
    ...this.pinnedTabs(),
    ...this.unpinnedTabs(),
  ]);

  /**
   * Tabs shown in the More menu.
   * Mandatory tabs are always listed first, then optional pinned, then optional unpinned.
   * Each group preserves canonical order from `tabs()`.
   */
  readonly moreMenuTabs = computed<TabItem[]>(() => {
    const all = this.tabs();
    const pinned = this.pinnedTabIds();
    const mandatory = this.effectiveMandatoryIds();
    const mandatorySet = new Set(mandatory);
    const optionalPinned = all.filter((t) => !mandatorySet.has(t.id) && pinned.has(t.id));
    const optionalUnpinned = all.filter((t) => !mandatorySet.has(t.id) && !pinned.has(t.id));
    const mandatoryTabs = all.filter((t) => mandatorySet.has(t.id));
    return [...mandatoryTabs, ...optionalPinned, ...optionalUnpinned];
  });

  /** True when any tab hidden from the main row has unread updates. */
  readonly hasOverflowUpdates = computed<boolean>(() =>
    this.overflowTabs().some(
      (tab) => Boolean(tab.hasUpdate) || this.hasCountBadge(tab),
    )
  );

  /** Sum of overflow-tab badge counts, rolled up onto the More button. */
  readonly overflowBadgeCount = computed<number>(() =>
    this.overflowTabs().reduce((sum, tab) => sum + (tab.badgeCount ?? 0), 0)
  );

  /**
   * When the user navigates to an unpinned tab (via the More menu), that tab is
   * temporarily appended to the end of the visible row so the user can see which
   * tab they are on. As soon as they navigate away, this is cleared and the tab
   * disappears from the row (it remains unpinned).
   */
  readonly temporarilyVisibleTabId = signal<string | null>(null);

  @ViewChildren('measureEl') measureEls!: QueryList<ElementRef<HTMLSpanElement>>;

  private _el = inject(ElementRef<HTMLElement>);
  private _zone = inject(NgZone);
  private _observer: ResizeObserver | null = null;

  /** Width reserved for the "More" button (px). Measured once after view init. */
  private _moreButtonWidth = 80;

  constructor() {
    effect(() => {
      const mandatory = this.effectiveMandatoryIds();
      // Touch storageContext so init waits for the input when provided.
      const stored = loadPinnedFromStorage(this.storageKey());

      if (!this._pinnedInitDone) {
        const base = new Set<string>(mandatory);
        if (stored) {
          for (const id of stored) base.add(id);
        }
        this.pinnedTabIds.set(base);
        this._pinnedInitDone = true;
        return;
      }

    });

    // Watch activeTabId: when it changes to an unpinned tab, make it temporarily
    // visible in the row. When it changes away from that tab, clear it.
    effect(() => {
      const activeId = this.activeTabId();
      const pinned = this.pinnedTabIds();
      const isUnpinned = !pinned.has(activeId);

      if (isUnpinned) {
        this.temporarilyVisibleTabId.set(activeId);
      } else {
        this.temporarilyVisibleTabId.set(null);
      }
      this._distribute();
    });
  }

  ngAfterViewInit(): void {
    this._zone.runOutsideAngular(() => {
      this._observer = new ResizeObserver(() => {
        this._distribute();
      });
      this._observer.observe(this._el.nativeElement);
    });
    this._distribute();
  }

  ngOnDestroy(): void {
    this._observer?.disconnect();
  }

  selectTab(id: string): void {
    this.showOverflow.set(false);
    this.tabSelect.emit(id);
  }

  toggleOverflow(event: Event): void {
    event.stopPropagation();
    this.showOverflow.update((v) => !v);
  }

  onDocumentClick(_event: MouseEvent): void {
    if (this.showOverflow()) {
      this.showOverflow.set(false);
    }
  }

  isActiveInOverflow(): boolean {
    return this.overflowTabs().some((t) => t.id === this.activeTabId());
  }

  moreButtonAriaLabel(): string {
    const overflowCount = this.overflowBadgeCount();
    if (overflowCount > 0) {
      return overflowCount === 1
        ? 'More tabs. 1 awaiting review.'
        : `More tabs. ${overflowCount} awaiting review.`;
    }
    return this.hasOverflowUpdates()
      ? 'More tabs. New updates available.'
      : 'More tabs. Pin tabs here to keep them in the main row.';
  }

  hasCountBadge(tab: TabItem): boolean {
    return (tab.badgeCount ?? 0) > 0;
  }

  badgeText(count: number): string {
    return count > 99 ? '99+' : String(count);
  }

  tabAriaLabel(tab: TabItem): string {
    const base =
      tab.id === this.temporarilyVisibleTabId()
        ? `${tab.label} (not pinned)`
        : tab.label;
    const count = tab.badgeCount ?? 0;
    if (count > 0) {
      return count === 1
        ? `${base}, 1 awaiting review`
        : `${base}, ${count} awaiting review`;
    }
    return tab.hasUpdate ? `${base}, new updates` : base;
  }

  isMandatory(tabId: string): boolean {
    return this.effectiveMandatoryIds().includes(tabId);
  }

  isPinned(tabId: string): boolean {
    return this.pinnedTabIds().has(tabId);
  }

  /**
   * Pin an optional tab. No-op if already pinned or mandatory.
   * Persists the new pinned set to localStorage.
   * Does NOT close the More menu so the user can continue managing pins.
   */
  pinTab(tabId: string): void {
    if (this.isMandatory(tabId) || this.isPinned(tabId)) return;
    const next = new Set(this.pinnedTabIds());
    next.add(tabId);
    this.pinnedTabIds.set(next);
    savePinnedToStorage(this.storageKey(), [...next], this.effectiveMandatoryIds());
    this._distribute();
  }

  /**
   * Unpin an optional tab. No-op for mandatory tabs.
   * Persists the new pinned set to localStorage.
   * Does NOT close the More menu so the user can continue managing pins.
   */
  unpinTab(tabId: string): void {
    if (this.isMandatory(tabId)) return;
    const next = new Set(this.pinnedTabIds());
    next.delete(tabId);
    this.pinnedTabIds.set(next);
    savePinnedToStorage(this.storageKey(), [...next], this.effectiveMandatoryIds());
    this._distribute();
  }

  togglePin(tabId: string, event: Event): void {
    event.stopPropagation();
    if (this.isPinned(tabId)) {
      this.unpinTab(tabId);
    } else {
      this.pinTab(tabId);
    }
  }

  private _distribute(): void {
    const hostWidth = this._el.nativeElement.getBoundingClientRect().width;
    if (hostWidth === 0) return;

    // Only pinned tabs are candidates for the visible row.
    // Unpinned tabs always live in More, except for the one temporarily-visible tab.
    const pinned = this.pinnedTabs();
    const unpinned = this.unpinnedTabs();
    const tempId = this.temporarilyVisibleTabId();

    // The temporarily-visible tab (if any) is appended after pinned tabs.
    // It is excluded from the unpinned list for overflow so it doesn't appear twice.
    const tempTab = tempId ? (this.tabs().find((t) => t.id === tempId) ?? null) : null;
    const unpinnedWithoutTemp = tempTab
      ? unpinned.filter((t) => t.id !== tempId)
      : unpinned;

    // Optional tabs must always remain manageable, even when all are pinned and fit.
    const hasMore = this.hasManageableTabs() || unpinnedWithoutTemp.length > 0;

    const spans = this.measureEls?.toArray() ?? [];
    const tabsInput = this.tabs();
    const indexMap = new Map<string, number>(tabsInput.map((t, i) => [t.id, i]));

    const getWidth = (tab: TabItem): number => {
      const idx = indexMap.get(tab.id) ?? -1;
      return idx >= 0 ? (spans[idx]?.nativeElement.getBoundingClientRect().width ?? 0) : 0;
    };

    const pinnedWidths = pinned.map(getWidth);

    // If widths aren't measured yet, show all pinned + temp and re-trigger on next frame.
    if (pinnedWidths.every((w) => w === 0) && pinned.length > 0) {
      this._zone.run(() => {
        this.visibleTabs.set(tempTab ? [...pinned, tempTab] : pinned);
        this.overflowTabs.set(unpinnedWithoutTemp);
      });
      return;
    }

    // Fit as many pinned tabs as possible. Reserve space for More whenever
    // optional tabs are manageable or unpinned tabs need navigation.
    // Also reserve space for the temp tab if one exists.
    const tempWidth = tempTab ? getWidth(tempTab) : 0;
    const countFittingPinnedTabs = (reserveMore: boolean): number => {
      let usedWidth = (reserveMore ? this._moreButtonWidth : 0) + tempWidth;
      let count = 0;
      for (let i = 0; i < pinned.length; i++) {
        const tabW = pinnedWidths[i] ?? 0;
        if (usedWidth + tabW > hostWidth) {
          break;
        }
        usedWidth += tabW;
        count++;
      }
      return count;
    };

    let fitCount = countFittingPinnedTabs(hasMore);
    // If mandatory-only tabs spill, reserve More and recalculate so its button also fits.
    if (!hasMore && fitCount < pinned.length) {
      fitCount = countFittingPinnedTabs(true);
    }

    // Pinned tabs that don't fit spill into More.
    const visiblePinned = pinned.slice(0, fitCount);
    const spilledPinned = pinned.slice(fitCount);

    // Temporarily-visible tab is always appended at the end of the visible row
    // (after all pinned tabs that fit). It does not compete with pinned tabs for space.
    const newVisible = tempTab ? [...visiblePinned, tempTab] : visiblePinned;
    const newOverflow = [...spilledPinned, ...unpinnedWithoutTemp];

    this._zone.run(() => {
      this.visibleTabs.set(newVisible);
      this.overflowTabs.set(newOverflow);
    });
  }
}
